import { trackPerformanceMetrics, IPerformanceMetrics } from "./analytics";

let telemetryInterval: any = null;
let longTaskCount = 0;
let maxCpuLagMs = 0;
let observerStarted = false;
let lastRafTime: number = 0;

/**
 * Initializes performance observers for CPU long tasks on platforms that support PerformanceObserver.
 */
function initPerformanceObserver(): void {
  if (observerStarted) return;

  if (typeof window !== "undefined" && "PerformanceObserver" in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          longTaskCount += 1;
          if (entry.duration > maxCpuLagMs) {
            maxCpuLagMs = entry.duration;
          }
        }
      });
      observer.observe({ entryTypes: ["longtask"] });
      observerStarted = true;
    } catch {
      // Long Tasks API not supported in this browser/environment
    }
  }

  // Event loop / RAF lag detection fallback for both Web and Mobile
  if (typeof requestAnimationFrame !== "undefined") {
    lastRafTime = Date.now();
    const checkLag = () => {
      const now = Date.now();
      const delta = now - lastRafTime;
      lastRafTime = now;

      // Delta > 100ms indicates frame freeze / CPU blocking (typical frame is ~16.6ms)
      if (delta > 100) {
        const lag = delta - 16;
        if (lag > maxCpuLagMs) {
          maxCpuLagMs = lag;
        }
      }
      requestAnimationFrame(checkLag);
    };
    requestAnimationFrame(checkLag);
  }
}

/**
 * Samples current device memory metrics.
 * @returns {Partial<IPerformanceMetrics>} Current memory statistics.
 */
export function sampleMemoryMetrics(): Partial<IPerformanceMetrics> {
  const result: Partial<IPerformanceMetrics> = {};

  if (typeof window !== "undefined" && (window.performance as any)?.memory) {
    const mem = (window.performance as any).memory;
    result.memoryUsageBytes = mem.usedJSHeapSize;
    result.memoryTotalBytes = mem.totalJSHeapSize;
    if (mem.totalJSHeapSize > 0) {
      result.memoryHeapRatio = mem.usedJSHeapSize / mem.totalJSHeapSize;
    }
  }

  return result;
}

/**
 * Samples current device network metrics.
 * @returns {Partial<IPerformanceMetrics>} Current network connection statistics.
 */
export function sampleNetworkMetrics(): Partial<IPerformanceMetrics> {
  const result: Partial<IPerformanceMetrics> = {};

  if (typeof navigator !== "undefined" && (navigator as any)?.connection) {
    const conn = (navigator as any).connection;
    result.networkEffectiveType = conn.effectiveType || conn.type;
    result.networkDownlinkMbps = conn.downlink;
    result.networkRttMs = conn.rtt;
  }

  return result;
}

/**
 * Samples current CPU responsiveness and long task telemetry.
 * @returns {Partial<IPerformanceMetrics>} CPU lag and long task metrics.
 */
export function sampleCpuMetrics(): Partial<IPerformanceMetrics> {
  const result: Partial<IPerformanceMetrics> = {
    cpuLagMs: maxCpuLagMs,
    longTaskCount: longTaskCount,
  };

  // Reset max lag for the next reporting interval
  maxCpuLagMs = 0;
  return result;
}

/**
 * Collects a full device telemetry snapshot (memory, network, CPU) and logs it to Google Analytics.
 */
export function reportDevicePerformanceSnapshot(): void {
  const metrics: IPerformanceMetrics = {
    ...sampleMemoryMetrics(),
    ...sampleNetworkMetrics(),
    ...sampleCpuMetrics(),
  };

  trackPerformanceMetrics(metrics);
}

/**
 * Starts the periodic background telemetry monitor.
 * Reports memory, network, and CPU metrics to Google Analytics at a given interval (default 60s).
 * @param {number} [intervalMs=60000] - Sample reporting frequency in milliseconds.
 */
export function startTelemetryMonitor(intervalMs: number = 60000): void {
  if (telemetryInterval) return;

  initPerformanceObserver();

  // Initial report after 5 seconds to capture initial load performance
  setTimeout(() => {
    reportDevicePerformanceSnapshot();
  }, 5000);

  // Periodic recurring report
  telemetryInterval = setInterval(() => {
    reportDevicePerformanceSnapshot();
  }, intervalMs);

  console.info(`[Telemetry] Telemetry monitor active (${intervalMs}ms interval)`);
}

/**
 * Stops the periodic telemetry monitor.
 */
export function stopTelemetryMonitor(): void {
  if (telemetryInterval) {
    clearInterval(telemetryInterval);
    telemetryInterval = null;
  }
}
